import { DropResult } from 'react-beautiful-dnd';

import { IActiveIndices, IMappingRules } from '@components/mapping-panel/store';
import { EMappingRuleType, ERuleActionType } from '@components/mapping-panel/typed';
import { EventRuleTypes, MappingEventTypes } from '@src/common/types/empTypes';
import { EStringOp } from '@src/emp-3.0/types';
import { isEmptyOrNotEmptyOp } from '@src/emp-3.0/utils/checks';

import { EInputType } from '../components/RuleBuilder/KeyValueBuilder/typed';
import { IRuleItem } from '../components/RuleBuilder/RuleGroup/typed';
import {
  getEmptyMetadataGroup,
  getEmptyMetadataItem,
  getEmptyRuleGroup,
  getEmptyRuleItem,
} from './initializeUtils';

export const addAndRuleItem = (mappingRules: IMappingRules[], orIndex: number) => {
  mappingRules[orIndex].rules.push([getEmptyRuleItem()]);
  return mappingRules;
};

export const addOrRuleItem = (mappingRules: IMappingRules[], orIndex: number) => {
  const l = mappingRules[orIndex].rules.length;
  mappingRules[orIndex].rules[l - 1].push(getEmptyRuleItem());
  return mappingRules;
};

export const deleteRuleItem = (
  mappingRules: IMappingRules[],
  orIndex: number,
  subAndIndex: number,
  subOrIndex: number,
) => {
  mappingRules[orIndex].rules[subAndIndex].splice(subOrIndex, 1);
  if (mappingRules[orIndex].rules[subAndIndex].length === 0) {
    mappingRules[orIndex].rules.splice(subAndIndex, 1);
  }
  if (mappingRules[orIndex].rules.length === 0) {
    mappingRules[orIndex].rules.push([getEmptyRuleItem()]);
  }
  return mappingRules;
};

export const addRuleGroup = (mappingRules: IMappingRules[]) => {
  mappingRules.push(getEmptyRuleGroup());
  return mappingRules;
};

export const deleteRuleGroup = (mappingRules: IMappingRules[], orIndex: number) => {
  mappingRules.splice(orIndex, 1);
  if (mappingRules.length === 0) {
    mappingRules.push(getEmptyRuleGroup());
  }
  return mappingRules;
};

export const updateRulesByLogicOperator = (
  mappingRules: IMappingRules[],
  orIndex: number,
  subAndIndex: number,
  subOrIndex?: number,
) => {
  if (subOrIndex === undefined) {
    // Switching the logical operator "AND" to "OR" updates the rules.
    const andRules = mappingRules[orIndex].rules.splice(subAndIndex, 1);
    mappingRules[orIndex].rules[subAndIndex - 1].push(...andRules[0]);
  } else {
    // Switching the logical operator "OR" to "AND" updates the rules.
    const newAndRules = mappingRules[orIndex].rules[subAndIndex].splice(subOrIndex);
    mappingRules[orIndex].rules.splice(subAndIndex + 1, 0, newAndRules);
  }
  return mappingRules;
};

export const addMetadataGroup = (mappingRules: IMappingRules[], orIndex: number) => {
  if (mappingRules[orIndex].metadata) {
    mappingRules[orIndex].metadata?.push([getEmptyMetadataItem()]);
  } else {
    mappingRules[orIndex] = { ...mappingRules[orIndex], ...getEmptyMetadataGroup() };
  }
  return mappingRules;
};

export const addOrMetadataItem = (
  mappingRules: IMappingRules[],
  orIndex: number,
  subAndIndex: number,
  key?: string,
) => {
  mappingRules[orIndex]?.metadata?.[subAndIndex]?.push(getEmptyMetadataItem(key));
  return mappingRules;
};

export const deleteMetadataGroup = (
  mappingRules: IMappingRules[],
  orIndex: number,
  subAndIndex: number,
) => {
  mappingRules[orIndex].metadata?.splice(subAndIndex, 1);
  return mappingRules;
};

export const deleteMetadataItem = (
  mappingRules: IMappingRules[],
  orIndex: number,
  subAndIndex: number,
  subOrIndex: number,
) => {
  mappingRules[orIndex].metadata?.[subAndIndex]?.splice(subOrIndex, 1);
  if (mappingRules[orIndex].metadata?.[subAndIndex]?.length === 0) {
    mappingRules[orIndex].metadata?.splice(subAndIndex, 1);
  }
  return mappingRules;
};

export const updateRules = (
  mappingRules: IMappingRules[],
  orIndex: number,
  subAndIndex: number,
  subOrIndex: number,
  type: EInputType,
  value: string,
) => {
  const ruleGroup = mappingRules?.[orIndex];
  const ruleItem = ruleGroup.rules?.[subAndIndex]?.[subOrIndex];
  const metadataItem = ruleGroup.metadata && ruleGroup.metadata?.[subAndIndex]?.[subOrIndex];
  switch (type) {
    case EInputType.EVENT_NAME:
      ruleGroup.eventName = value;
      break;
    case EInputType.KEY:
      ruleItem.tagKey = value;
      break;
    case EInputType.OPERATOR:
      ruleItem.tagValue.operator = value as EStringOp;
      if (isEmptyOrNotEmptyOp(value)) {
        ruleItem.tagValue.value = '';
      }
      break;
    case EInputType.VALUE:
      ruleItem.tagValue.value = value;
      break;
    case EInputType.METADATA_NAME:
      if (ruleGroup.metadata) {
        ruleGroup.metadata[subAndIndex] = ruleGroup.metadata[subAndIndex].map((subOrMetadata) => {
          subOrMetadata.key = value;
          return subOrMetadata;
        });
      }
      break;
    case EInputType.METADATA_OPERATOR:
      if (metadataItem) {
        metadataItem.op = value as EStringOp;
      }
      break;
    case EInputType.METADATA_VALUE:
      if (metadataItem) {
        metadataItem.value = value ? [value] : [];
      }
      break;
  }
  return mappingRules;
};

export const rulesChange = (
  rules: IMappingRules[],
  actionType: ERuleActionType,
  orIndex = 0,
  subAndIndex = 0,
  subOrIndex?: number,
  metadataKey?: string,
): IMappingRules[] => {
  switch (actionType) {
    case ERuleActionType.DELETE_ALL:
      rules = [getEmptyRuleGroup()];
      break;
    case ERuleActionType.DELETE_RULE_GROUP:
      rules = [...deleteRuleGroup(rules, orIndex)];
      break;
    case ERuleActionType.DELETE_RULE_ITEM:
      rules = [...deleteRuleItem(rules, orIndex, subAndIndex, subOrIndex ?? 0)];
      break;
    case ERuleActionType.ADD_RULE_GROUP:
      rules = [...addRuleGroup(rules)];
      break;
    case ERuleActionType.ADD_AND_RULE_ITEM:
      rules = [...addAndRuleItem(rules, orIndex)];
      break;
    case ERuleActionType.ADD_OR_RULE_ITEM:
      rules = [...addOrRuleItem(rules, orIndex)];
      break;
    case ERuleActionType.LOGIC_OPERATOR_CHANGE:
      rules = [...updateRulesByLogicOperator(rules, orIndex, subAndIndex, subOrIndex)];
      break;
    case ERuleActionType.ADD_METADATA_GROUP:
      rules = [...addMetadataGroup(rules, orIndex)];
      break;
    case ERuleActionType.ADD_OR_METADATA_ITEM:
      rules = [...addOrMetadataItem(rules, orIndex, subAndIndex, metadataKey)];
      break;
    case ERuleActionType.DELETE_METADATA_GROUP:
      rules = [...deleteMetadataGroup(rules, orIndex, subAndIndex)];
      break;
    case ERuleActionType.DELETE_METADATA_ITEM:
      rules = [...deleteMetadataItem(rules, orIndex, subAndIndex, subOrIndex ?? 0)];
      break;
    default:
      break;
  }
  return rules;
};

export const dragRulesChange = (
  result: DropResult,
  ruleType: EMappingRuleType,
  rules: IMappingRules[],
): { rules: IMappingRules[]; activeIndices: IActiveIndices } | undefined => {
  const { destination, source } = result;

  if (!destination) {
    return;
  }

  if (destination.droppableId === source.droppableId && destination.index === source.index) {
    return;
  }

  try {
    const destinationObj = JSON.parse(destination.droppableId);
    const sourceObj = JSON.parse(source.droppableId);
    const { orIndex, subAndIndex: destinationSubAndIndex, key } = destinationObj;
    const { subAndIndex: sourceSubAndIndex } = sourceObj;
    const sourceIndex = source.index;
    const destinationIndex = destination.index;

    if (key && key === 'Metadata') {
      const temp = rules[orIndex]?.metadata?.[sourceSubAndIndex]?.splice(sourceIndex, 1);
      temp &&
        rules[orIndex]?.metadata?.[destinationSubAndIndex]?.splice(destinationIndex, 0, ...temp);
    } else {
      const temp = rules[orIndex].rules[sourceSubAndIndex].splice(sourceIndex, 1);
      rules[orIndex].rules[destinationSubAndIndex].splice(destinationIndex, 0, ...temp);
    }

    const activeIndicesProps: IActiveIndices = {
      orIndex,
      ruleType,
    };
    if (key && key === 'Metadata') {
      activeIndicesProps.metadataItems = [
        {
          subAndIndex: destinationSubAndIndex,
          subOrIndex: destinationIndex,
        },
      ];
    } else {
      activeIndicesProps.ruleItems = [
        {
          subAndIndex: destinationSubAndIndex,
          subOrIndex: destinationIndex,
          tagKey: true,
          tagValue: true,
        },
      ];
    }

    return { rules, activeIndices: activeIndicesProps };
  } catch (e) {
    console.log('Drag drop end error: ', e);
    return;
  }
};

export const transformRulesToState = (
  rules: MappingEventTypes[][] | undefined,
): IMappingRules[] => {
  if (!rules) {
    return [getEmptyRuleGroup()];
  }
  const res: IMappingRules[] = [];
  rules.forEach((andGroup) => {
    andGroup.forEach((orGroup) => {
      const ruleGroup: IMappingRules = {
        eventName: '',
        rules: [],
        metadata: orGroup.metadata ?? [],
      };
      ruleGroup.eventName = orGroup.value[0];
      orGroup.mapping_rules.forEach((subAndGroup) => {
        const andRuleGroup: IRuleItem[] = [];
        subAndGroup.forEach((subOrGroup) => {
          const keys = subOrGroup.tag_key.value.length === 0 ? [null] : subOrGroup.tag_key.value;
          keys.forEach((tagKey) => {
            const values =
              subOrGroup.tag_value.value.length === 0 ? [null] : subOrGroup.tag_value.value;
            values.forEach((tagValue) => {
              const ruleItem = {
                tagKey,
                tagValue: {
                  operator: subOrGroup.tag_value.op,
                  value: tagValue,
                },
              };
              andRuleGroup.push(ruleItem);
            });
          });
        });
        ruleGroup.rules.push(andRuleGroup);
      });
      if (ruleGroup.rules.length === 0) {
        ruleGroup.rules.push([getEmptyRuleItem()]);
      }
      res.push(ruleGroup);
    });
  });
  return res;
};

export const transformStateToRules = (state: IMappingRules[]): MappingEventTypes[] => {
  const res: MappingEventTypes[] = [];
  state.forEach((stateRuleGroup) => {
    const mappingRules: EventRuleTypes[][] = [];
    stateRuleGroup.rules.forEach((subAndGroup) => {
      const subAndRules: EventRuleTypes[] = [];
      subAndGroup.forEach((ruleItem) => {
        subAndRules.push({
          tag_key: {
            value: ruleItem.tagKey ? [ruleItem.tagKey] : [],
            op: EStringOp.EQ,
          },
          tag_value: {
            op: ruleItem.tagValue.operator,
            value: ruleItem.tagValue.value ? [ruleItem.tagValue.value] : [],
          },
        });
      });
      mappingRules.push(subAndRules);
    });
    const ruleGroup: MappingEventTypes = {
      key: 'all_event_name',
      op: EStringOp.EQ,
      value: stateRuleGroup.eventName ? [stateRuleGroup.eventName] : [],
      mapping_rules: mappingRules,
      metadata: stateRuleGroup?.metadata ?? [],
    };
    res.push(ruleGroup);
  });
  return res;
};
